import {
  Document,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  BorderStyle,
  Packer,
  TabStopPosition,
  TabStopType,
} from 'docx';
import { saveAs } from 'file-saver';

export interface ResumeExperience {
  title: string;
  company: string;
  dates: string;
  achievements: string[];
}

export interface ResumeEducation {
  degree: string;
  institution: string;
  year: string;
}

export interface ResumeSections {
  professional_summary?: string;
  core_competencies?: string[];
  experience?: ResumeExperience[];
  education?: ResumeEducation[];
  certifications?: string[];
  technical_skills?: Record<string, string[]>;
}

export interface ProfileInfo {
  full_name?: string;
  email?: string;
  phone?: string;
  location?: string;
  linkedin_url?: string;
}

const FONT = 'Calibri';
const FONT_SIZE = 22; // half-points, so 11pt
const HEADING_SIZE = 26; // 13pt

function sectionHeading(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 240, after: 80 },
    border: {
      bottom: { style: BorderStyle.SINGLE, size: 1, color: '333333' },
    },
    children: [
      new TextRun({
        text: text.toUpperCase(),
        bold: true,
        font: FONT,
        size: HEADING_SIZE,
        color: '1a1a1a',
      }),
    ],
  });
}

function bulletPoint(text: string): Paragraph {
  return new Paragraph({
    bullet: { level: 0 },
    spacing: { after: 40 },
    children: [
      new TextRun({ text, font: FONT, size: FONT_SIZE }),
    ],
  });
}

export async function generateResumeDocx(
  sections: ResumeSections,
  profile: ProfileInfo,
  jobTitle: string,
  company: string,
): Promise<void> {
  const paragraphs: Paragraph[] = [];

  // Header — Name
  const name = profile.full_name || 'Candidate Name';
  paragraphs.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 40 },
      children: [
        new TextRun({
          text: name,
          bold: true,
          font: FONT,
          size: 36, // 18pt
          color: '1a1a1a',
        }),
      ],
    }),
  );

  // Contact line
  const contactParts: string[] = [];
  if (profile.email) contactParts.push(profile.email);
  if (profile.phone) contactParts.push(profile.phone);
  if (profile.location) contactParts.push(profile.location);
  if (profile.linkedin_url) contactParts.push(profile.linkedin_url);

  if (contactParts.length) {
    paragraphs.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: contactParts.join('  |  '),
            font: FONT,
            size: 20, // 10pt
            color: '555555',
          }),
        ],
      }),
    );
  }

  // Professional Summary
  if (sections.professional_summary) {
    paragraphs.push(sectionHeading('Professional Summary'));
    paragraphs.push(
      new Paragraph({
        spacing: { after: 120 },
        children: [
          new TextRun({
            text: sections.professional_summary,
            font: FONT,
            size: FONT_SIZE,
            italics: true,
          }),
        ],
      }),
    );
  }

  // Core Competencies
  if (sections.core_competencies?.length) {
    paragraphs.push(sectionHeading('Core Competencies'));
    // Display as comma-separated with bullet separators
    paragraphs.push(
      new Paragraph({
        spacing: { after: 120 },
        children: [
          new TextRun({
            text: sections.core_competencies.join('  \u2022  '),
            font: FONT,
            size: FONT_SIZE,
          }),
        ],
      }),
    );
  }

  // Experience
  if (sections.experience?.length) {
    paragraphs.push(sectionHeading('Professional Experience'));
    for (const exp of sections.experience) {
      // Title + Company line
      paragraphs.push(
        new Paragraph({
          spacing: { before: 120, after: 40 },
          tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
          children: [
            new TextRun({ text: exp.title, bold: true, font: FONT, size: FONT_SIZE }),
            new TextRun({ text: ` — ${exp.company}`, font: FONT, size: FONT_SIZE }),
            new TextRun({ text: '\t', font: FONT, size: FONT_SIZE }),
            new TextRun({ text: exp.dates, font: FONT, size: FONT_SIZE, color: '666666', italics: true }),
          ],
        }),
      );
      // Achievement bullets
      for (const achievement of exp.achievements) {
        paragraphs.push(bulletPoint(achievement));
      }
    }
  }

  // Education
  if (sections.education?.length) {
    paragraphs.push(sectionHeading('Education'));
    for (const edu of sections.education) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 60 },
          tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
          children: [
            new TextRun({ text: edu.degree, bold: true, font: FONT, size: FONT_SIZE }),
            new TextRun({ text: ` — ${edu.institution}`, font: FONT, size: FONT_SIZE }),
            new TextRun({ text: '\t', font: FONT, size: FONT_SIZE }),
            new TextRun({ text: edu.year, font: FONT, size: FONT_SIZE, color: '666666' }),
          ],
        }),
      );
    }
  }

  // Certifications
  if (sections.certifications?.length) {
    paragraphs.push(sectionHeading('Certifications'));
    for (const cert of sections.certifications) {
      paragraphs.push(bulletPoint(cert));
    }
  }

  // Technical Skills
  if (sections.technical_skills && Object.keys(sections.technical_skills).length) {
    paragraphs.push(sectionHeading('Technical Skills'));
    for (const [category, skills] of Object.entries(sections.technical_skills)) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 40 },
          children: [
            new TextRun({ text: `${category}: `, bold: true, font: FONT, size: FONT_SIZE }),
            new TextRun({ text: skills.join(', '), font: FONT, size: FONT_SIZE }),
          ],
        }),
      );
    }
  }

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: { top: 720, bottom: 720, left: 900, right: 900 }, // ~0.5in/0.625in
          },
        },
        children: paragraphs,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const safeName = name.replace(/\s+/g, '_');
  saveAs(blob, `${safeName}_Resume_${company.replace(/\s+/g, '_')}.docx`);
}
